<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import LibraryImage from '@/components/library/LibraryImage/LibraryImage.vue'
import { slotRenders } from '@/components/primitives/slot-renders'
import { CONTEXT_KIND_ICONS, excerptLead } from '../AppChatPanel/context-types'
import type { ChatMessageAttachment, MessageContext, TranscriptMessageRole } from '@/types/chat'

const props = defineProps<{
  /** Who wrote the message; decides the bubble's side and shape. */
  from: TranscriptMessageRole
  /** `thread` (default) is a message in a conversation · `quoted` is a question shown inside another product's chat: borderless, small text, on the content colour. */
  variant?: 'thread' | 'quoted'
  /** Images attached to a user message; rendered above the text bubble. */
  attachments?: readonly ChatMessageAttachment[]
  /** What the composer chips showed when the user sent the message; rendered as a caption row above the bubble. */
  context?: MessageContext
  /** The user's message was just sent: it rises into the thread from the composer. */
  arriving?: boolean
}>()

const { t } = useI18n()

const bubbleClass = computed(() =>
  props.variant === 'quoted'
    ? 'rounded-8 bg-bg-content px-8 py-6 text-body-sm'
    : 'max-w-(--message-max-width) rounded-4 bg-bg-surface-accent px-8 py-6 text-body',
)

const contextPassage = computed(() =>
  props.context?.item?.excerpt
    ? t('chat.context.passage', { text: excerptLead(props.context.item.excerpt) })
    : '',
)

const rootClass = computed(() =>
  props.from === 'user' ? 'flex justify-end' : 'group flex flex-col gap-4',
)
</script>

<template>
  <div :class="rootClass">
    <template v-if="from === 'user'">
      <div
        :class="[
          'flex min-w-0 max-w-full flex-col items-end gap-4',
          variant === 'quoted' ? 'max-w-4/5' : '',
          arriving ? 'message-rise' : '',
        ]"
      >
        <div
          v-if="attachments?.length"
          class="flex max-w-(--message-max-width) flex-wrap justify-end gap-6"
        >
          <LibraryImage
            v-for="(attachment, index) in attachments"
            :key="attachment.itemId ?? `${index}-${attachment.src}`"
            :item-id="attachment.itemId"
            :src="attachment.src"
            :alt="attachment.alt ?? t('chat.composer.attachedImage')"
            class="size-64 rounded-4 border border-border object-cover"
          />
        </div>
        <div
          v-if="context?.screen || context?.item"
          class="icon-xs flex min-w-0 max-w-full flex-wrap justify-end gap-x-4 gap-y-1 text-caption text-text-secondary"
        >
          <span v-if="context.screen" class="inline-flex min-w-0 max-w-full items-center gap-2">
            <component
              :is="CONTEXT_KIND_ICONS.section"
              aria-hidden="true"
              class="shrink-0 text-accent-blue"
            />
            <span class="truncate">{{ context.screen }}</span>
          </span>
          <span v-if="context.item" class="inline-flex min-w-0 max-w-full items-center gap-2">
            <component
              :is="CONTEXT_KIND_ICONS[context.item.kind]"
              aria-hidden="true"
              class="shrink-0"
            />
            <span class="truncate">{{ context.item.ref }}</span>
            <span v-if="contextPassage" class="truncate">&middot; {{ contextPassage }}</span>
          </span>
        </div>
        <div
          v-if="slotRenders($slots.default)"
          :class="bubbleClass"
          class="text-text-primary whitespace-pre-wrap"
        >
          <slot />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="text-body text-text-primary">
        <slot />
      </div>
      <div
        v-if="$slots.actions"
        class="opacity-0 transition-opacity duration-fast group-hover:opacity-100 group-focus-within:opacity-100"
      >
        <slot name="actions" />
      </div>
    </template>
  </div>
</template>

<style>
.message-rise {
  animation: message-rise var(--transition-duration-slower) var(--ease-spring) both;
}
@keyframes message-rise {
  from {
    opacity: 0;
    translate: 0 calc(var(--spacing-12) - var(--spacing-1));
    scale: 0.97;
  }
}
@media (prefers-reduced-motion: reduce) {
  .message-rise {
    animation: none;
  }
}
</style>
